import { Box, Chip, Stack, Typography } from '@mui/material'
import { matchSorter } from 'match-sorter'
import React, { useMemo, useState } from 'react'

import { SearchField } from 'components/common/SearchField'
import { PendingNamespaceAccessRequestAlert } from 'components/configurations/common/PendingNamespaceAccessRequestAlert'
import Icon from 'components/Icon'
import { muiTheme } from 'mui-theme'
import { SharedAssetRequestStatus } from 'types/api/sharedAssets/requests'

import type { OwnedK8sClusterAsset } from 'types/api/sharedAssets/sharedAssets'

export const OwnedClusterNamespacesDrawerContent: React.FC<{
  activeAsset: OwnedK8sClusterAsset
}> = ({ activeAsset }) => {
  const [searchValue, setSearchValue] = useState('')

  const { assetId, clusterName, availableNamespaces } = activeAsset
  const pendingNamespaceRequests = activeAsset.requests.filter(
    (request) => request.currentStatus === SharedAssetRequestStatus.REQUESTED
  )

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
  }

  const filteredNamespaces = useMemo(() => {
    if (!searchValue) {
      return availableNamespaces
    }

    return matchSorter(availableNamespaces, searchValue, {
      threshold: matchSorter.rankings.CONTAINS
    })
  }, [availableNamespaces, searchValue])

  return (
    <Stack
      data-testid={`owned-cluster-namespaces-drawer-content-${assetId}`}
      sx={{ rowGap: 4 }}
    >
      <Stack sx={{ rowGap: 1 }}>
        <Typography variant="h6">
          <Icon
            css={{ marginRight: muiTheme.spacing(1) }}
            icon="dharmachakra"
          />
          {clusterName}
        </Typography>
        <Typography variant="body1">Namespaces owned by your team</Typography>
        {pendingNamespaceRequests.length > 0 && (
          <PendingNamespaceAccessRequestAlert title="A team is waiting for namespace sharing approval" />
        )}
      </Stack>
      <SearchField
        fullWidth
        helperText="Search by namespace"
        label="Search"
        value={searchValue}
        onChange={handleSearchChange}
      />
      <Box>
        {filteredNamespaces.map((namespace) => {
          return (
            <Chip
              key={namespace}
              data-testid={`chip-shared-namespace-${namespace}`}
              label={namespace}
              sx={{ margin: 0.5, width: 'fit-content' }}
            />
          )
        })}
      </Box>
    </Stack>
  )
}
